import React from 'react';
import SEO from '../components/SEO';

const TIMELINE = [
  { step: '1', label: 'Submit Request', desc: 'Email support@flightbookkaro.com or use My Bookings to request a cancellation.' },
  { step: '2', label: 'Review (24–48 hrs)', desc: 'We verify eligibility based on airline fare rules and your ticket type.' },
  { step: '3', label: 'Airline Approval', desc: 'We submit the refund request to the airline. This may take 5–10 business days.' },
  { step: '4', label: 'Refund Issued', desc: 'Once approved, the refund is credited to your original payment method.' },
];

const MATRIX = [
  { type: 'Fully Refundable Fare', window: 'Any time before departure', refund: 'Full fare minus service fee', time: '7–14 business days' },
  { type: 'Partially Refundable Fare', window: 'Before departure', refund: 'Airline penalty + service fee deducted', time: '10–21 business days' },
  { type: 'Non-Refundable Fare', window: '—', refund: 'No refund (taxes may be recoverable)', time: '—' },
  { type: 'Award / Free Ticket', window: 'Before departure', refund: 'Points restored (varies by airline)', time: '14–21 business days' },
  { type: 'Flight Cancelled by Airline', window: 'Automatic', refund: 'Full refund including service fee', time: '7–14 business days' },
];

export default function RefundPolicyPage() {
  return (
    <div>
      <SEO
        title="Refund Policy"
        canonical="/refund-policy"
        description="FlightBooKaro Refund Policy — understand how to request refunds, eligibility criteria, timelines, and what fees may apply to your flight cancellation."
        keywords="refund policy, flight cancellation refund, ticket refund, flightbookkaro refund"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 sm:py-20 px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
          <span>💰</span> Last Revised: June 1, 2025
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Refund Policy</h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          We strive to make refunds as straightforward as possible. Timelines depend on your fare type and the airline's rules.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16 space-y-10">

        {/* 24-hour rule */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <span className="text-3xl">✅</span>
            <div>
              <h2 className="text-lg font-bold text-green-800 mb-1">24-Hour Free Cancellation</h2>
              <p className="text-green-700 text-sm">
                If your departure is more than <strong>7 days away</strong>, you may cancel within <strong>24 hours of booking</strong> for a full refund of the base fare. Our service fee is non-refundable. This applies to all fare types.
              </p>
            </div>
          </div>
        </div>

        {/* Refund matrix */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-900">Refund Eligibility by Fare Type</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3">Fare Type</th>
                  <th className="text-left px-5 py-3">Cancel Window</th>
                  <th className="text-left px-5 py-3">Refund Amount</th>
                  <th className="text-left px-5 py-3">Processing Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {MATRIX.map((row) => (
                  <tr key={row.type} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 font-medium text-gray-800">{row.type}</td>
                    <td className="px-5 py-4 text-gray-600">{row.window}</td>
                    <td className="px-5 py-4 text-gray-600">{row.refund}</td>
                    <td className="px-5 py-4 text-gray-600">{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Process timeline */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-6">How to Get a Refund</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIMELINE.map((item) => (
              <div key={item.step} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-700 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{item.label}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Non-refundable taxes note */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Important Notes</h2>
          {[
            { icon: '⚠️', title: 'Taxes & Surcharges', desc: 'Government taxes are generally recoverable even on non-refundable fares. Submit a separate request for tax reclaim if applicable.' },
            { icon: '🚫', title: 'Non-Refundable Situations', desc: 'No-shows without prior cancellation, name change requests, and voluntary downgrades are not eligible for refunds.' },
            { icon: '🔁', title: 'Airline Vouchers', desc: 'Some airlines offer travel credits instead of cash refunds. We will notify you of your options before processing.' },
            { icon: '💳', title: 'Refund Method', desc: 'Refunds are always returned to the original payment method. We cannot issue refunds via a different card or bank account.' },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-3">
              <span className="text-xl mt-0.5">{item.icon}</span>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-gray-700 font-semibold mb-1">Need to cancel a booking?</p>
          <p className="text-gray-500 text-sm mb-4">Email us with your booking ID and we'll handle the rest.</p>
          <a
            href="mailto:support@flightbookkaro.com?subject=Refund Request"
            className="inline-block bg-gradient-to-r from-violet-700 to-amber-500 text-white font-semibold px-6 py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity"
          >
            Request a Refund →
          </a>
        </div>
      </div>
    </div>
  );
}
